// ============================================================
//  src/pages/bots/tabs/PanelTab.js
// ============================================================
import React, { useEffect, useState } from 'react';
import { getPanel, savePanelField, updatePanelValue, deletePanelField } from '../../../api/client';
import toast from 'react-hot-toast';
import './tabs.css';

const TYPES = ['text','number','password','textarea','url','email','select'];

// ── Field row ─────────────────────────────────────────────────
function FieldRow({ field, onEdit, onDelete, onValueChange }) {
  const [val, setVal]       = useState(field.pValue ?? '');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await updatePanelValue({ bId: field.pBotId, pKey: field.pKey, pValue: val });
      if (res.data?.success) toast.success('Value saved');
      else toast.error(res.data?.message ?? 'Failed');
    } catch { toast.error('Network error'); }
    setSaving(false);
  }

  const isSecret = field.pIsSecret == 1;

  return (
    <div className="panel-field-row">
      <div className="panel-field-meta">
        <div className="panel-field-title">
          {field.pIcon && <span style={{ marginRight: 5 }}>{field.pIcon}</span>}
          {field.pTitle}
          {isSecret && <span className="secret-badge">secret</span>}
        </div>
        <div className="panel-field-key">{field.pKey}</div>
        {field.pDescription && <div className="panel-field-desc">{field.pDescription}</div>}
      </div>
      <div className="panel-field-input-row">
        <input
          className="form-input"
          type={isSecret ? 'password' : (field.pType === 'number' ? 'number' : 'text')}
          value={val}
          onChange={e => setVal(e.target.value)}
          placeholder={`Enter ${field.pTitle}`}
        />
        <button className="btn btn-primary" style={{ flexShrink: 0 }} onClick={handleSave} disabled={saving}>
          {saving ? <span className="spinner" /> : 'Save'}
        </button>
        <button className="icon-btn" onClick={() => onEdit(field)} title="Edit field">✎</button>
        <button className="icon-btn danger" onClick={() => onDelete(field)} title="Delete field">✕</button>
      </div>
    </div>
  );
}

// ── Field modal ───────────────────────────────────────────────
function FieldModal({ bId, field, onClose, onSaved }) {
  const isEdit = !!field;
  const [form, setForm] = useState({
    pKey:         field?.pKey         ?? '',
    pTitle:       field?.pTitle       ?? '',
    pDescription: field?.pDescription ?? '',
    pType:        field?.pType        ?? 'text',
    pTag:         field?.pTag         ?? '',
    pIcon:        field?.pIcon        ?? '',
    pIsSecret:    field?.pIsSecret    ?? 0,
    pOrder:       field?.pOrder       ?? 0,
    pValue:       field?.pValue       ?? '',
  });
  const [saving, setSaving] = useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.pKey.trim())   return toast.error('Key is required');
    if (!form.pTitle.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      const res = await savePanelField({ bId, ...form });
      if (res.data?.success) {
        toast.success(isEdit ? 'Field updated' : 'Field created');
        onSaved();
      } else {
        toast.error(res.data?.message ?? 'Failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message ?? 'Network error');
    }
    setSaving(false);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Field' : 'Add Panel Field'}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Key</label>
              <input className="form-input" placeholder="host_api" value={form.pKey}
                onChange={e => set('pKey', e.target.value)} disabled={isEdit} />
              <div className="form-hint">Used in code: Panel.key_name</div>
            </div>
            <div className="form-group">
              <label className="form-label">Title</label>
              <input className="form-input" placeholder="API Host" value={form.pTitle}
                onChange={e => set('pTitle', e.target.value)} />
            </div>
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-input" value={form.pType} onChange={e => set('pType', e.target.value)}>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Tag</label>
              <input className="form-input" placeholder="optional tag" value={form.pTag}
                onChange={e => set('pTag', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-input" placeholder="What this field is for" value={form.pDescription}
              onChange={e => set('pDescription', e.target.value)} />
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Icon</label>
              <input className="form-input" placeholder="🔑 or icon name" value={form.pIcon}
                onChange={e => set('pIcon', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Order</label>
              <input className="form-input" type="number" value={form.pOrder}
                onChange={e => set('pOrder', parseInt(e.target.value))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Default Value</label>
            <input className="form-input" value={form.pValue}
              onChange={e => set('pValue', e.target.value)} />
          </div>
          <div className="form-group">
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
              <input type="checkbox" checked={form.pIsSecret == 1}
                onChange={e => set('pIsSecret', e.target.checked ? 1 : 0)} />
              <span style={{ fontSize:13, fontWeight:600 }}>Secret field (mask value)</span>
            </label>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <span className="spinner" /> : (isEdit ? 'Save Changes' : 'Add Field')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────
export default function PanelTab({ bId }) {
  const [fields, setFields]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  async function load() {
    try {
      const res = await getPanel(bId);
      if (res.data?.success) setFields(res.data.fields ?? []);
    } catch { toast.error('Failed to load panel'); }
    setLoading(false);
  }

  useEffect(() => { load(); }, [bId]);

  async function handleDelete() {
    try {
      await deletePanelField(bId, deleteTarget.pId);
      toast.success('Field deleted');
      setFields(prev => prev.filter(f => f.pId !== deleteTarget.pId));
    } catch { toast.error('Failed to delete'); }
    setDeleteTarget(null);
  }

  if (loading) return <div className="tab-loading"><span className="spinner spinner-dark" /> Loading…</div>;

  return (
    <div className="tab-content">
      <div className="tab-toolbar">
        <div>
          <div className="tab-title">Panel Fields</div>
          <div className="tab-sub">Configure your bot's runtime variables</div>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}>+ Add Field</button>
      </div>

      {fields.length === 0 ? (
        <div className="tab-empty">
          No panel fields yet.{' '}
          <button className="link-btn" onClick={() => setModal('new')}>Add your first field →</button>
        </div>
      ) : (
        <div className="panel-fields-list">
          {fields.map(f => (
            <FieldRow
              key={f.pId}
              field={f}
              onEdit={f => setModal(f)}
              onDelete={f => setDeleteTarget(f)}
            />
          ))}
        </div>
      )}

      {modal && (
        <FieldModal
          bId={bId}
          field={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}

      {deleteTarget && (
        <div className="modal-backdrop" onClick={() => setDeleteTarget(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Delete Field</div>
              <button className="modal-close" onClick={() => setDeleteTarget(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 20 }}>Delete field <strong>{deleteTarget.pKey}</strong>? Cannot be undone.</p>
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={() => setDeleteTarget(null)}>Cancel</button>
                <button className="btn" style={{ background:'var(--danger)',color:'#fff' }} onClick={handleDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
